import * as React from 'react'
import { useHistory, useParams } from 'react-router'
import { Centered, Expander, List, Loader } from '../../../components'
import { useAppSelector } from '../../../reducers'
import { storeOfflinePercentage } from '../../../reducers/stores'
import type Channel from '../../../types/Channel'
import Device, { DeviceGroup } from '../../../types/DeviceMain'
import type { StoreWithDevices } from '../../../types/StoreMain'
import { StoreUtils } from '../../../utils'
import ChannelsFormExtra from '../FormsEdit/Channels'
import ChannelDevicesTitle from './ChannelDevicesTitle'
import { colors, Routes } from '../../../constants'
import type { ContentURLParams } from './types'

interface ChannelsExpanderProps {
    isOpen: boolean;
    setChildrenExpanderChannelsHeight: (val: number) => void;
}

export default function ChannelsExpander({
    isOpen,
    setChildrenExpanderChannelsHeight,
}: ChannelsExpanderProps) {
    const displayGroups = useAppSelector(state => state.device.displayGroups) as DeviceGroup[]
    const isSelectingChannel = useAppSelector(state => state.content.isWorking.channelSingle)
    const isWorkingChannelDevices = useAppSelector(state => state.content.isWorking.channelDevices)
    const isWorkingPlaylists = useAppSelector(state => state.content.isWorking.channelSingle)
    const selectedChannel = useAppSelector<Channel>(state => state.content.selected.channels)

    // URL query params
    const { estate: selectedEstate } = useParams<ContentURLParams>()
    const history = useHistory()
    
    let devices = selectedChannel?.devices as Device[] | undefined

    devices = devices?.sort((a, b) => {
        return a.store_code.localeCompare(b.store_code)
            || a.display_group - b.display_group
            || a.id.localeCompare(b.id)
    })

    const devicesDisplay = (item: Device) => {
        const display_group = displayGroups.find((group) => group.id === item.display_group)
        const { id, store_code: storeCode } = item || {}
        return `${storeCode} - ${id} (${display_group?.name})`
    }

    const isLoading = isWorkingPlaylists || isWorkingChannelDevices

    // get stores list with devices
    const storesList = devices?.reduce((out: StoreWithDevices[], device): StoreWithDevices[] => {
        const store = device.store as StoreWithDevices | undefined

        if (store) {
            const storeWithDevices = {
                ...store,
                devices: store.devices
                    ? [...store.devices, device]
                    : [device],
            }
            const existedStoreIndex = out.findIndex(
                (d) => store != null && d.code === store?.code,
            )

            if (existedStoreIndex !== -1) {

                out[existedStoreIndex] = {
                    ...out[existedStoreIndex],
                    devices: out[existedStoreIndex].devices ? [...out[existedStoreIndex].devices, device] : [device],
                }

                return out
            }

            return [...out, storeWithDevices]
        }
        
        return out
    }, [])

    const storesDisplay = (item: StoreWithDevices) => {
        const { name, devices } = item || {}
        const offline = storeOfflinePercentage({ devices })
        const offlineDevices = StoreUtils.calculateofflinePercentage(offline)
        return `${name} - ${offlineDevices}% online `
    }

    const onDeviceClick = (index: number, e: React.MouseEvent): void => {
            if (typeof e.stopPropagation === 'function') {
                e.stopPropagation()
            }
        
            if (!selectedChannel) {
                return
            }

            const { devices } = selectedChannel

            if (!devices) {
                return
            }
    
            if (devices.length > index) {
                const { store_code: storeCode, id: uid } = devices[index] || {}
    
                const goTo = Routes.buildRoute(Routes.ESTATE_STORE, {
                    estate: selectedEstate,
                    code: storeCode,
                    search: uid,
                })

                return history.push(goTo)
            }
        }
    
    const onStoreClick = (index: number, e: React.MouseEvent): void => {
        if (typeof e.stopPropagation === 'function') {
            e.stopPropagation()
        }

        // const { selectedChannel, history, selectedEstate } = this.props

        if (!selectedChannel) {
            return
        }

        const { devices } = selectedChannel
        const storesList = devices?.reduce(
            (out: StoreWithDevices[], device: Device): StoreWithDevices[] => {
                const store = device.store as StoreWithDevices | undefined

                if (store) {
                    let storeWithDevices = {
                        ...store,
                        devices: store.devices
                            ? [...store.devices, device]
                            : [device],
                    }
                    const existedStoreIndex = out.findIndex(
                        (d) => store != null && d.code === store?.code
                    )

                    if (existedStoreIndex !== -1) {
                        out[existedStoreIndex] = storeWithDevices

                        return out
                    }

                    return [...out, storeWithDevices]
                }
                
                return out
            },
            [],
        )

        if (!devices) {
            return
        }

        if (storesList && devices.length > index) {
            const { code } = storesList[index] || {}

            const goTo = Routes.buildRoute(Routes.ESTATE_STORE, {
                estate: selectedEstate,
                code,
            })
            return history.push(goTo)
        }
    }

    const onChangeChildrenExpanderHeight = (height: number) =>
        setChildrenExpanderChannelsHeight(height + 1)

    if (!selectedChannel) {
        if (!isSelectingChannel) {
            return null
        }

        return (
            <Centered horizontal>
                <Loader color={colors.white} style={{ transform: 'scale(0.7)' }} />
            </Centered>
        )
    }

    return (
        <>
            <ChannelsFormExtra isOpen={isOpen} channel={selectedChannel} />
            <Expander
                loading={isLoading}
                onlyLeftChevron
                header={<ChannelDevicesTitle isOpen={isOpen} isLoading={isLoading} />}
                onChangeHeight={onChangeChildrenExpanderHeight}
                color="white"
                noPropagation
                bold
            >
                {devices && devices?.length > 0 && (
                    <List
                        data={devices}
                        display={devicesDisplay}
                        onClickRow={onDeviceClick}
                        noBorders
                        noBackGround
                    />
                )}
            </Expander>
            <Expander
                loading={isLoading}
                onlyLeftChevron
                header="stores"
                onChangeHeight={onChangeChildrenExpanderHeight}
                color="white"
                noPropagation
                bold
            >
                {storesList && storesList?.length > 0 && (
                    <List
                        data={storesList}
                        display={storesDisplay}
                        onClickRow={onStoreClick}
                        noBorders
                        noBackGround
                    />
                )}
            </Expander>
        </>
    )
}
